// 053 — Referencia, Derivación, Contrarreferencia y Referencia Inversa (fila
// 35 de 8.3-bis + Fase 5, Tanda 4: los bloques oficiales de las 2 mitades,
// ver `bloques053Datos.ts`). Mismo patrón que 007 (Interconsulta), destino
// distinto (otro establecimiento, no otro especialista interno).
//
// FHIR: la mitad Referencia/Derivación crea un `ServiceRequest` (destino en
// `performer`, tipo en `category`, motivos en `orderDetail`, diagnósticos en
// `reasonCode`, resumen/hallazgos en `note`); la mitad Contrarreferencia/
// Referencia inversa crea un `Communication` (el tipo en `topic` — antes las
// dos se confundían) con un `payload` por sección. Cada mitad se guarda solo
// si tiene contenido y `onGuardado` reporta únicamente los pares de las
// mitades efectivamente guardadas en esta llamada, igual que 007.
import type { Communication, Patient, ServiceRequest } from '@medplum/fhirtypes';
import { useMedplum, useResource } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { FormularioDiscretoProps, Par } from './index';
import { AreaDictado } from './AreaDictado';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional, recursoRespuesta } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { Bloque, CamposSimples, SelectSimple } from './camposSimples';
import { conCodigoCie, MARCAS_PRE_DEF } from './camposSimplesDatos';
import { SelectorDiagnosticoConMarca, type DiagnosticoConMarca } from './MarcaDiagnostico';
import { codeDeDiagnostico } from './SelectorDiagnosticoCie10';
import { SelectorRecurso } from './SelectorRecurso';
import {
  CAMPOS_DESTINO_CONTRARREFERENCIA_053,
  CAMPOS_DESTINO_REFERENCIA_053,
  CAMPOS_ORIGEN_CONTRARREFERENCIA_053,
  CAMPOS_ORIGEN_REFERENCIA_053,
  CAMPOS_PACIENTE_053,
  MOTIVOS_053,
  OPCIONES_TIPO_CONTRARREFERENCIA_053,
  OPCIONES_TIPO_REFERENCIA_053,
  contrarreferenciaTieneDatos,
  estadoInicialBitacora053,
  etiquetaTipoContrarreferencia053,
  etiquetaTipoReferencia053,
  faltantesObligatorios053,
  motivosSeleccionados053,
  ambasMitadesConDatos053,
  notasExtraReferencia053,
  payloadExtraContrarreferencia053,
  performerTypeReferencia053,
  requesterResponsable053,
  senderContrarreferencia053,
  paresDeContrarreferencia053,
  paresDeReferencia053,
  referenciaSeGuarda,
  referenciaTieneDatos,
  valoresPacienteDesdePatient053,
  type EstadoBitacora053,
  type ValorSiNo053,
} from './bloques053Datos';

const CODIGO = '053';

function ListaDiagnosticos({ prefijo, titulo, marcaLabel, diagnosticos, onChange }: { readonly prefijo: string; readonly titulo: string; readonly marcaLabel: string; readonly diagnosticos: readonly DiagnosticoConMarca[]; readonly onChange: (d: readonly DiagnosticoConMarca[]) => void }): JSX.Element {
  return (
    <>
      {diagnosticos.map((d, i) => (
        <SelectorDiagnosticoConMarca
          key={`${prefijo}-${i}`}
          label={`${titulo} ${i + 1}`}
          marcaLabel={marcaLabel}
          marcas={MARCAS_PRE_DEF}
          value={d}
          onChange={(v) => onChange(diagnosticos.map((x, j) => (j === i ? v : x)))}
        />
      ))}
    </>
  );
}

export function Formulario053({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('hub.formulario.053');
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): Referencia/contrarreferencia: el SERVICIO del papel es el del establecimiento de DESTINO, no el de la atención actual.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { sinServicio: true });

  const [estado, setEstado] = useState<EstadoBitacora053>(() => estadoInicialBitacora053());
  const [referenciaOrigen, setReferenciaOrigen] = useState<ServiceRequest>();
  // Si la Communication falla después de crear el ServiceRequest, el reintento no lo duplica.
  const [referenciaCreada, setReferenciaCreada] = useState<ServiceRequest>();
  const patient = useResource<Patient>(subject);
  const pacientePrecargado = useRef(false);
  // Teléfono y residencia del bloque A: se precargan del Patient una sola vez, cuando carga; lo que el usuario ya escribió se respeta.
  useEffect(() => {
    if (!patient || pacientePrecargado.current) return;
    pacientePrecargado.current = true;
    const precargado = valoresPacienteDesdePatient053(patient);
    setEstado((e) => {
      const escrito = Object.fromEntries(Object.entries(e.referencia.paciente).filter(([, v]) => v.trim() !== ''));
      return { ...e, referencia: { ...e.referencia, paciente: { ...precargado, ...escrito } } };
    });
  }, [patient]);
  const ref = estado.referencia;
  const contra = estado.contrarreferencia;
  const setRef = (parcial: Partial<typeof ref>): void => setEstado({ ...estado, referencia: { ...ref, ...parcial } });
  const setContra = (parcial: Partial<typeof contra>): void => setEstado({ ...estado, contrarreferencia: { ...contra, ...parcial } });

  const opcionesSiNo = [
    { valor: 'si', etiqueta: t('formularios.f053.si') },
    { valor: 'no', etiqueta: t('formularios.f053.no') },
  ];

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const ahora = new Date().toISOString();
      const pares: Par[] = [];
      const lote = new LoteTransaccional();
      let solicitudNueva: ServiceRequest | undefined;

      if (referenciaTieneDatos(ref) && !referenciaSeGuarda(ref)) {
        throw new Error(t('formularios.f053.faltaDestino'));
      }
      const hayContra = contrarreferenciaTieneDatos(contra);
      if (ambasMitadesConDatos053(ref, contra)) throw new Error(t('formularios.f053.guardarPorSeparado'));
      if (!referenciaSeGuarda(ref) && !hayContra) {
        throw new Error(t('formularios.f053.sinDatos'));
      }
      const faltan = faltantesObligatorios053(ref, contra);
      if (faltan.length > 0) throw new Error(t('formularios.f053.faltaTipo', { campos: faltan.join('; ') }));

      if (referenciaSeGuarda(ref)) {
        const destino = ref.destino.establecimiento.trim();
        const tipoTexto = etiquetaTipoReferencia053(ref.tipo);
        const motivos = motivosSeleccionados053(ref.motivos);
        // Las dos notas de siempre (resumen, hallazgos) primero; después las líneas con origen, destino completo y evaluación.
        const notas = [ref.resumenCuadroClinico.trim() && `${t('formularios.f053.resumenTitulo')}: ${ref.resumenCuadroClinico.trim()}`, ref.hallazgos.trim() && `${t('formularios.f053.hallazgosTitulo')}: ${ref.hallazgos.trim()}`].filter((n): n is string => !!n);
        notas.push(...notasExtraReferencia053(ref));
        const tipoDestino = performerTypeReferencia053(ref);
        const solicitante = requesterResponsable053(medplum.getProfile());
        const diagnosticos = ref.diagnosticos.filter((d) => d.diagnostico.texto.trim() !== '');
        if (!referenciaCreada) solicitudNueva = lote.crear<ServiceRequest>({
          resourceType: 'ServiceRequest',
          status: 'active',
          intent: 'order',
          category: [{ text: ref.tipo === 'derivacion' ? 'Derivación a otro establecimiento' : 'Referencia a otro establecimiento' }],
          code: { text: `${tipoTexto} a ${destino}` },
          subject,
          encounter,
          authoredOn: ahora,
          performer: [{ display: destino }],
          performerType: tipoDestino,
          requester: solicitante,
          orderDetail: motivos.length > 0 ? motivos.map((m) => ({ text: m })) : undefined,
          reasonCode: diagnosticos.length > 0 ? diagnosticos.map((d) => codeDeDiagnostico(d.diagnostico)) : undefined,
          note: notas.length > 0 ? notas.map((text) => ({ text })) : undefined,
        });
        pares.push(...paresDeReferencia053(t, ref));
      }

      if (hayContra) {
        const secciones: [string, string][] = [
          [t('formularios.f053.resumenTitulo'), contra.resumenCuadroClinico],
          [t('formularios.f053.hallazgosTitulo'), contra.hallazgos],
          [t('formularios.f053.tratamientosRealizadosTitulo'), contra.tratamientosRealizados],
          [t('formularios.f053.tratamientoRecomendadoTitulo'), contra.tratamientoRecomendado],
        ];
        const payload = secciones.filter(([, texto]) => texto.trim() !== '').map(([etiqueta, texto]) => ({ contentString: `${etiqueta}: ${texto.trim()}` }));
        const diagnosticos = contra.diagnosticos.filter((d) => d.diagnostico.texto.trim() !== '').map((d) => `${t('formularios.f053.diagnosticoContraTitulo')}: ${conCodigoCie(d.diagnostico)}${d.marca ? ` (${d.marca.toUpperCase()})` : ''}`);
        const destino = (contra.destino.establecimiento ?? '').trim();
        const solicitanteContra = requesterResponsable053(medplum.getProfile());
        lote.crear<Communication>({
          resourceType: 'Communication',
          status: 'completed',
          category: [{ coding: [{ system: 'http://logiacare.ec/fhir/formularios-msp', code: CODIGO }] }],
          topic: { text: etiquetaTipoContrarreferencia053(contra.tipo) },
          subject,
          encounter,
          basedOn: referenciaOrigen?.id ? [{ reference: `ServiceRequest/${referenciaOrigen.id}` }] : undefined,
          recipient: destino ? [{ display: destino }] : undefined,
          sender: senderContrarreferencia053(contra),
          // El profesional que contrarrefiere y firma es el de la sesión; `sender` sigue siendo el establecimiento.
          note: solicitanteContra ? [{ authorReference: solicitanteContra, text: t('formularios.f053.responsableContraTitulo') }] : undefined,
          sent: ahora,
          payload: [...payload, ...[...diagnosticos, ...payloadExtraContrarreferencia053(contra)].map((contentString) => ({ contentString }))],
        });
        pares.push(...paresDeContrarreferencia053(t, contra));
      }

      const respuesta = await lote.enviar(medplum);
      if (solicitudNueva) setReferenciaCreada(recursoRespuesta(respuesta, 0, solicitudNueva));
      onGuardado(pares, datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <h4 className="border-b border-border pb-1 text-base font-medium text-hce-ink">{t('formularios.f053.mitadReferencia')}</h4>

      <Bloque titulo={t('formularios.f053.origenTitulo')}>
        <CamposSimples prefijoId="f053-ref-origen" campos={CAMPOS_ORIGEN_REFERENCIA_053} valores={ref.origen} onChange={(v) => setRef({ origen: v })} />
        <CamposSimples prefijoId="f053-ref-paciente" campos={CAMPOS_PACIENTE_053} valores={ref.paciente} columnas={4} onChange={(v) => setRef({ paciente: v })} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <SelectSimple id="f053-ref-tipo" etiqueta={t('formularios.f053.tipoReferencia')} valor={ref.tipo} opciones={OPCIONES_TIPO_REFERENCIA_053} onChange={(v) => setRef({ tipo: v })} />
        </div>
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-hce-ink">{t('formularios.f053.motivoTitulo')}</legend>
          {MOTIVOS_053.map((m, i) => (
            <div key={m.clave} className="flex items-center gap-2">
              <Checkbox id={`f053-motivo-${m.clave}`} checked={ref.motivos[m.clave] ?? false} onCheckedChange={(v) => setRef({ motivos: { ...ref.motivos, [m.clave]: v === true } })} />
              <Label htmlFor={`f053-motivo-${m.clave}`} className="font-normal">
                {i + 1}. {m.etiqueta}
              </Label>
            </div>
          ))}
        </fieldset>
      </Bloque>

      <Bloque titulo={t('formularios.f053.destinoTitulo')}>
        <CamposSimples prefijoId="f053-ref-destino" campos={CAMPOS_DESTINO_REFERENCIA_053} valores={ref.destino} onChange={(v) => setRef({ destino: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f053.resumenTitulo')}>
        <AreaDictado id="f053-ref-resumen" etiqueta={t('formularios.f053.resumenTitulo')} valor={ref.resumenCuadroClinico} filas={5} onChange={(v) => setRef({ resumenCuadroClinico: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f053.hallazgosTitulo')}>
        <AreaDictado id="f053-ref-hallazgos" etiqueta={t('formularios.f053.hallazgosTitulo')} valor={ref.hallazgos} onChange={(v) => setRef({ hallazgos: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f053.diagnosticoTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f053.diagnosticoNota')}</p>
        <ListaDiagnosticos prefijo="ref" titulo={t('formularios.f053.diagnosticoTitulo')} marcaLabel={t('formularios.f053.marcaDiagnostico')} diagnosticos={ref.diagnosticos} onChange={(d) => setRef({ diagnosticos: d })} />
      </Bloque>

      <Bloque titulo={t('formularios.f053.responsableTitulo')}>
        <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
      </Bloque>

      <Bloque titulo={t('formularios.f053.evaluacionTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f053.evaluacionNota')}</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <SelectSimple id="f053-ref-justificada" etiqueta={t('formularios.f053.referenciaJustificada')} valor={ref.referenciaJustificada} opciones={opcionesSiNo} onChange={(v) => setRef({ referenciaJustificada: v as ValorSiNo053 })} />
          <SelectSimple id="f053-der-justificada" etiqueta={t('formularios.f053.derivacionJustificada')} valor={ref.derivacionJustificada} opciones={opcionesSiNo} onChange={(v) => setRef({ derivacionJustificada: v as ValorSiNo053 })} />
        </div>
      </Bloque>

      <div className="my-2 border-t border-border" />

      <h4 className="border-b border-border pb-1 text-base font-medium text-hce-ink">{t('formularios.f053.mitadContrarreferencia')}</h4>

      <Bloque titulo={t('formularios.f053.origenContraTitulo')}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <SelectSimple id="f053-contra-tipo" etiqueta={t('formularios.f053.tipoContrarreferencia')} valor={contra.tipo} opciones={OPCIONES_TIPO_CONTRARREFERENCIA_053} onChange={(v) => setContra({ tipo: v })} />
        </div>
        <CamposSimples prefijoId="f053-contra-origen" campos={CAMPOS_ORIGEN_CONTRARREFERENCIA_053} valores={contra.origen} onChange={(v) => setContra({ origen: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f053.destinoContraTitulo')}>
        <CamposSimples prefijoId="f053-contra-destino" campos={CAMPOS_DESTINO_CONTRARREFERENCIA_053} valores={contra.destino} onChange={(v) => setContra({ destino: v })} />
        <SelectorRecurso<'ServiceRequest'>
          resourceType="ServiceRequest"
          subject={subject}
          label={t('formularios.f053.referenciaOrigen')}
          etiqueta={(r) => r.code?.text ?? r.id ?? ''}
          value={referenciaOrigen}
          onChange={setReferenciaOrigen}
        />
      </Bloque>

      <Bloque titulo={t('formularios.f053.contraTitulo')}>
        <AreaDictado id="f053-contra-resumen" etiqueta={t('formularios.f053.resumenTitulo')} valor={contra.resumenCuadroClinico} filas={4} onChange={(v) => setContra({ resumenCuadroClinico: v })} />
        <AreaDictado id="f053-contra-hallazgos" etiqueta={t('formularios.f053.hallazgosTitulo')} valor={contra.hallazgos} onChange={(v) => setContra({ hallazgos: v })} />
        <AreaDictado id="f053-contra-tratamientos" etiqueta={t('formularios.f053.tratamientosRealizadosTitulo')} valor={contra.tratamientosRealizados} onChange={(v) => setContra({ tratamientosRealizados: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f053.diagnosticoContraTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f053.diagnosticoNota')}</p>
        <ListaDiagnosticos prefijo="contra" titulo={t('formularios.f053.diagnosticoContraTitulo')} marcaLabel={t('formularios.f053.marcaDiagnostico')} diagnosticos={contra.diagnosticos} onChange={(d) => setContra({ diagnosticos: d })} />
      </Bloque>

      <Bloque titulo={t('formularios.f053.tratamientoRecomendadoTitulo')}>
        <AreaDictado id="f053-contra-recomendado" etiqueta={t('formularios.f053.tratamientoRecomendadoTitulo')} valor={contra.tratamientoRecomendado} onChange={(v) => setContra({ tratamientoRecomendado: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f053.responsableContraTitulo')}>
        <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
